<template>
    <div
        ref="componentTable"
        class="component-table"
    >
        <base-table-layout
            :headers="headersCloned"
            :items="sortedAndFilteredItems"
            :theme="theme"
            :loading="loading"
            :show-expand="showExpand"
            @click:row="item => $emit('click:row', item)"
            @keydown:tr="onKeydownTr"
        >
            <!-- 부모의 slot 을 그대로 자식 컴포넌트로 전달합니다 -->
            <template
                v-for="(index, name) in $scopedSlots"
                v-slot:[name]="data"
            >
                <slot
                    :name="name"
                    v-bind="data"
                />
            </template>

            <!-- th slot -->
            <!-- 상위에서 전달받은 header.** Slot 을 여기서 매핑합니다. -->
            <template
                v-for="(headerWithName) in headerWithHeaderSlotName"
                v-slot:[headerWithName.headerSlotName]
            >
                <!-- item.checkbox slot 일 경우 -->
                <template v-if="headerWithName.key === 'checkbox'">
                    <v-checkbox
                        v-model="allChecked"
                        :indeterminate="indeterminate"
                        :key="'header.' + headerWithName.key"
                        hide-details
                    ></v-checkbox>
                </template>

                <template v-if="headerWithName.key === 'expanded'">
                </template>

                <table-th
                    v-else
                    :key="'header.' + headerWithName.key"
                    :text="headerWithName.text"
                    :sortable="headerWithName.sortable"
                    :sorted="sortingKey && sortingKey === headerWithName.key"
                    :sort-desc="sortDesc"
                    @sort:up="onSortUp(headerWithName)"
                    @sort:down="onSortDown(headerWithName)"
                    @sort:origin="onSortOrigin"
                />
            </template>

            <!-- td slot -->
            <!-- 상위에서 전달받은 item.** Slot 을 여기서 매핑합니다. -->
            <template
                v-for="(headerWithName) in headerWithItemSlotName"
                v-slot:[headerWithName.itemSlotName]="{ item, header, index }"
            >
                <!-- item.checkbox slot 일 경우 -->
                <template v-if="headerWithName.key === 'checkbox'">
                    <v-checkbox
                        v-model="item.checked"
                        :key="'item.' + item.key + '.' + headerWithName.key"
                        hide-details
                    ></v-checkbox>
                </template>

                <template v-if="headerWithName.key === 'expanded'">
                    <form-table-td-expanded
                        :key="'item.' + item.key + '.' + headerWithName.key"
                        :icon="headerWithName.icon"
                        :src="headerWithName.src"
                        @click="toggleExpand(item, headerWithName)"
                        :expanded="item.expanded"
                    >
                    </form-table-td-expanded>
                </template>

                <form-table-component-td
                    v-else
                    :key="'item.' + item.key + '.' + headerWithName.key"
                    :editable="header.editable"
                    :align="headerWithName.align"
                    :on="inputEventHandlerObject[item.key + '.' + headerWithName.key]"
                    @on="obj => setValueReactiveTd(item.key + '.' + headerWithName.key, obj)"
                    @keydown:td="onKeydownTd"
                >
                    <slot
                        :name="headerWithName.itemSlotName"
                        v-bind="{ item: item.raw,
                                  header: headerWithName,
                                  on: tdEventHandlerObject[item.key + '.' + headerWithName.key],
                        }"
                    >
                        <!-- Editable 이 true 일 경우 미리 정의된 컴포넌트를 사용합니다. -->
                        <component
                            :is="getFormComponent(headerWithName.component)"
                            v-if="headerWithName.editable"
                            class="form-table__input__container"
                            :placeholver="headerWithName.placeholder"
                            :options-data="headerWithName.options"
                            :editable="headerWithName.editable"
                            :value="headerWithName.read ? headerWithName.read(byString(item.raw, headerWithName.key), item.raw) : byString(item.raw, headerWithName.key)"
                            :on="tdEventHandlerObject[item.key + '.' + headerWithName.key]"
                            @on="obj => setValueReactiveInput(item.key + '.' + headerWithName.key, obj)"
                            @next="keypressArrow(40, editableCols)"
                            @input="v => {headerWithName.write ? headerWithName.write(v, item.raw) : assign(item.raw, headerWithName.key, v), headerWithName.update && headerWithName.update(item.raw)}"
                        />

                        <!-- Only Readable 인 경우 텍스트를 출력합니다 -->
                        <template v-else>
                            {{ headerWithName.read ? headerWithName.read(byString(item.raw, headerWithName.key), item.raw) : byString(item.raw, headerWithName.key) }}
                        </template>
                    </slot>
                </form-table-component-td>
            </template>

            <!-- td item.append slot -->
            <!-- 아이콘을 위한 slot 영역을 정의합니다. -->
            <template
                v-for="(headerWithName, col) in headerWithItemSlotNameAppend"
                v-slot:[headerWithName.itemAppendSlotName]="{ item, header, index }"
            >
                <div
                    :key="'item.append' + item.key + '.' + headerWithName.key"
                    class="form-table__body__td__append">
                    <div v-if="!!headerWithName.tooltip && headerWithName.tooltip.src && headerWithName.tooltip.src(byString(item.raw, headerWithName.key), item.raw)">
                        <div
                                class="form-table__body__td__append__tootip"
                        >
                            <form-table-td-tooltip
                                    :tooltip="headerWithName.tooltip"
                                    :item="item"
                                    :header="header"
                            >
                            </form-table-td-tooltip>
                        </div>
                    </div>
                    <div v-if="!!headerWithName.icons">
                        <div
                                class="form-table__body__td__append__icons"
                        >
                            <template v-for="(icon, index) in headerWithName.icons">
                                <form-table-td-icon
                                        v-if="((icon.src && icon.src(byString(item.raw, headerWithName.key), item.raw))
                                        || (icon.icon && icon.icon(byString(item.raw, headerWithName.key), item.raw)))"
                                        :key="'item.append' + item.key + '.' + headerWithName.key + '.icon-' + index"
                                        :icon="icon"
                                        :item="item"
                                        :header="header"
                                >
                                </form-table-td-icon>
                            </template>
                        </div>
                    </div>
                </div>
            </template>

            <!-- item 확장 slot -->
            <template v-slot:expanded-item="{ headers, item, index }">
                <slot name="expanded-item" v-bind="{ headers, item, index }">
                    No Content
                </slot>
            </template>
        </base-table-layout>
    </div>
</template>

<script>
import BaseTableLayout from "../base-table-layout";
import FormTableProvider from "../../form-table-provider";
import {FORM_TABLE_TYPE} from "../../form-table-types";
import TableTh from "../part/TableTh";
import ComponentTableKeyHelper
    from "./mixins/ComponentTableKeyHelper";
import ComponentTablePropsMixin
    from "./mixins/ComponentTablePropsMixin";
import FormTableComponentTd from "./FormTableComponentTd";
import ComponentTableSortAndSearchHelper
    from "./mixins/ComponentTableSortAndSearchHelper";
import TrModel from "../../model/TrModel";
import tableUtilManager from "../../plugins/common/util/table-util-manager";
import FormTableTdIcon from "./FormTableTdIcon";
import ComponentTableCheckboxMixin
    from "@/assets/plugins/mps-form-table/component/component-table/mixins/ComponentTableCheckboxMixin";
import FormTableTdTooltip from "@/assets/plugins/mps-form-table/component/component-table/FormTableTdTooltip";
import FormTableTdExpanded from "@/assets/plugins/mps-form-table/component/component-table/FormTableTdExpanded";
import ComponentTableExpandMixin
    from "@/assets/plugins/mps-form-table/component/component-table/mixins/ComponentTableExpandMixin";

export default {
    name: "ComponentTable",
    components: {
        FormTableTdExpanded,
        FormTableTdTooltip, FormTableTdIcon, FormTableComponentTd, TableTh, BaseTableLayout},
    mixins: [ComponentTablePropsMixin, ComponentTableKeyHelper
        , ComponentTableSortAndSearchHelper, ComponentTableCheckboxMixin, ComponentTableExpandMixin],
    data() {
        return {
            formProvider: new FormTableProvider(this.provider),
            mappedItems: [],
            tdEventHandlerObject: {},
            inputEventHandlerObject: {},
            showExpand: false,
            headersCloned: [],
        };
    },
    computed: {
        /**
         * 한 row 에 editable 한 컬럼 수를 반환합니다.
         */
        editableCols() {
            return this.headersCloned.filter(e => e.editable).length;
        },

        /**
         * 헤더 슬랏에는 부모와 자식이 모두 존재합니다.(부모란 헤더의 row span 에서 위쪽을 가리킵니다.)
         */
        headersSerialized() {
            let headerHasChildren = this.headersCloned.filter(e => e.children);
            let children = [];
            headerHasChildren.forEach(e => children = children.concat(e.children));
            let headers = this.headersCloned;
            return headers.concat(children);
        },

        /**
         * 아이템 슬랏에는 부모가 없습니다.(부모란 헤더의 row span 에서 위쪽을 가리킵니다.)
         */
        headersSerializedGoa() {
            let headerHasChildren = this.headersCloned.filter(e => e.children);
            let children = [];
            headerHasChildren.forEach(e => children = children.concat(e.children));
            let headers = this.headersCloned.filter(e => !e.children);
            return headers.concat(children);
        },

        /**
         * 헤더의 동적 slot 바인딩을 위한 slot name 을 추가한 header 를 반환합니다.
         */
        headerWithHeaderSlotName() {
            return this.headersSerialized.map(e => {
                e.headerSlotName = "header." + e.key; // header slot name
                if(!e.options) e.options = {};
                return e;
            });
        },

        /**
         * 아이템의 동적 slot 바인딩을 위한 slot name 을 추가한 header 를 반환합니다.
         */
        headerWithItemSlotName() {
            return this.headersSerializedGoa.map(e => {
                e.itemSlotName = "item." + e.key; // item slot name
                if(!e.options) e.options = {};
                return e;
            });
        },

        /**
         * 아이콘 영억 slot name 을 추가한 header 를 반환합니다.
         */
        headerWithItemSlotNameAppend() {
            return this.headersSerializedGoa.filter(e => !tableUtilManager.isEmpty(e.icons) || (!!e.tooltip))
                .map(e => {
                    e.itemAppendSlotName = "item." + e.key + ".append"; // item.append slot name
                    return e;
                });
        },
    },
    watch: {
        headers() {
            this.initHeaders();
        },

        items() {
            if (Array.isArray(this.items) && this.items.length > 0) {
                this.items.forEach(e => this.mappedItems.push(new TrModel(e)));
            }
        },

        mappedItems() {
            this.$emit("update:items", this.mappedItems.map(e => e.raw));
        },
    },
    created() {
        this.mapping();
        this.initHeaders();
    },
    mounted() {
        this.mapping();
        this.initHeaders();
    },
    methods: {
        initHeaders() {
            this.headersCloned = [];
            this.headers.forEach(e => this.headersCloned.push(e));
            this.headersCloned.forEach(e => e.grow = e.grow || 0);
            let hasGrow = !!this.headersCloned.find(e => e.grow > 0);
            let first = true;
            for (let i = this.headersCloned.length - 1; i >= 0; i--) {
                const e = this.headersCloned[i];
                if (e.key === "checkbox" || e.key === "radio") {
                    e.width = 70;
                    e.grow = 0;
                } else if (e.key === "expanded") {
                    e.width = 70;
                    e.grow = 0;
                    this.showExpand = true;
                } else {
                    if (first && tableUtilManager.isEmpty(e.children) && !hasGrow) {
                        e.grow = e.grow || 1;
                        first = false;
                    }
                }

                if (!tableUtilManager.isEmpty(e.children)) e.grow = 0; // 자식이 있는 헤더는 grow 가 항상 0으로 고정됩니다.
            }
        },

        setValueReactiveTd(key, obj) {
            this.$set(this.tdEventHandlerObject, key, obj);
        },

        setValueReactiveInput(key, obj) {
            this.$set(this.inputEventHandlerObject, key, obj);
        },

        clear() {
            this.mappedItems.splice(0, this.mappedItems.length);
            this.tdEventHandlerObject = {};
            this.inputEventHandlerObject = {};
        },

        mapping() {
            if (Array.isArray(this.items) && this.items.length > 0) {
                this.mappedItems = this.items.map(e => new TrModel(e))
            }
        },

        /**
         * Type 에 해당하는 컴포넌트를 로드합니다.
         */
        getFormComponent(type) {
            let found = false;
            Object.keys(FORM_TABLE_TYPE).forEach(key => {
                if (type === FORM_TABLE_TYPE[key]) found = true;
            });
            if (!found) {
                return this.formProvider.getElement(FORM_TABLE_TYPE.TEXTAREA);
            } else {
                return this.formProvider.getElement(type);
            }
        },

        byString(o, s) {
            return tableUtilManager.byString(o, s);
        },

        assign(o, s, v) {
            tableUtilManager.assign(o, s, v);
        }
    }
}
</script>

<style scoped>
    .component-table {
        width: 100%;
        height: 100%;
        position: relative;
        border: 1px solid #ddd;
    }

    .form-table__body__td__append {
        display: flex;
        flex-direction: row;
        height: 100%;
        justify-content: start;
        align-items: center;
    }

    .form-table__body__td .form-table__body__td__append__icons {
        display: none;
        flex-direction: row;
        height: 100%;
        justify-content: start;
        align-items: center;
    }

    .form-table__body__tr:hover  .form-table__body__td__append__icons {
        display: flex !important;
    }

    .form-table__body__tr:hover  .form-table__body__td__append__tootip {
        display: flex !important;
    }

    >>> .v-input--selection-controls {
        margin-top: 0 !important;
        padding-top: 0 !important;
    }
</style>
